import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowBack, Edit, Save, Close } from '@mui/icons-material';
import api from '../../../services/api';
import styles from '../../../assets/styles/LayoutShared.module.css';
import viewStyles from '../../../assets/styles/LayoutSharedView.module.css';

// ─── Constantes ───────────────────────────────────────────────────────────────

const LABEL_CATEGORIA: Record<string, string> = {
  operacao_agricola: 'Operação Agrícola', custo: 'Custo', receita: 'Receita',
  manutencao: 'Manutenção', abastecimento: 'Abastecimento', outros: 'Outros',
};
const STATUS_LABELS: Record<string, string> = {
  registrado: 'Registrado', em_andamento: 'Em andamento',
  concluido: 'Concluído', cancelado: 'Cancelado', agendado: 'Agendado',
};
const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  agendado:     { color: '#9b59b6', bg: '#f5f0ff' },
  registrado:   { color: '#3498db', bg: '#ebf5fb' },
  em_andamento: { color: '#f39c12', bg: '#fef9e7' },
  concluido:    { color: '#00b37e', bg: '#f0fff8' },
  cancelado:    { color: '#999',    bg: '#f5f5f5' },
};
const EMOJI_CATEGORIA: Record<string, string> = {
  operacao_agricola: '🚜', custo: '💸', receita: '💰',
  manutencao: '🔧', abastecimento: '⛽', outros: '📝',
};
const COMBUSTIVEIS    = ['Diesel S10','Diesel Comum','Gasolina','Etanol','Arla 32','GNV'];
const TIPOS_MANUTENCAO = ['Borracharia','Troca de óleo','Elétrica','Mecânica','Hidráulica','Funilaria','Revisão','Outros'];

const TIPOS_OPERACAO_OPCOES = [
  'Plantio','Colheita','Adubação','Pulverização','Irrigação',
  'Capina/Roçagem','Poda','Aplicação','Transporte','Outros',
];

const CORES_TIPO_OP: Record<string, string> = {
  'Colheita': '#f39c12', 'Plantio': '#00b37e', 'Pulverização': '#3498db',
  'Adubação': '#9b59b6', 'Irrigação': '#1abc9c', 'Capina/Roçagem': '#e67e22',
  'Poda': '#8e44ad', 'Aplicação': '#2980b9', 'Transporte': '#607d8b', 'Outros': '#aaa',
};

const formatBRL = (v: any) => {
  const n = parseFloat(v || '0') || 0;
  return n > 0 ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const formatData = (raw: string | null | undefined, incluirHora = false) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getUTCDate()).padStart(2,'0');
  const mm = String(d.getUTCMonth()+1).padStart(2,'0');
  const aaaa = d.getUTCFullYear();
  if (incluirHora) {
    const hh = String(d.getUTCHours()).padStart(2,'0');
    const mi = String(d.getUTCMinutes()).padStart(2,'0');
    return `${dd}/${mm}/${aaaa} ${hh}:${mi}`;
  }
  return `${dd}/${mm}/${aaaa}`;
};

// ─── Estilos ──────────────────────────────────────────────────────────────────

const fieldStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid #00b37e', outline: 'none', fontSize: '0.9rem',
  background: '#f0fff8', boxSizing: 'border-box',
};

// ─── Campo somente leitura ────────────────────────────────────────────────────

const Campo = ({ label, value, children }: { label: string; value?: any; children?: React.ReactNode }) => {
  const conteudo = children ?? value;
  if (!conteudo && conteudo !== 0) return null;
  return (
    <div>
      <label style={{ fontSize:'0.78rem', color:'#888', fontWeight:600, textTransform:'uppercase' }}>{label}</label>
      <div style={{ margin:'4px 0 0', fontWeight:500 }}>{conteudo}</div>
    </div>
  );
};

// ─── Campo editável ───────────────────────────────────────────────────────────

interface EditProps {
  label: string;
  field: string;
  value: any;
  type?: string;
  options?: { value: string; label: string }[];
  onChange: (field: string, val: any) => void;
  placeholder?: string;
}

const CampoEdit: React.FC<EditProps> = ({ label, field, value, type = 'text', options, onChange, placeholder }) => (
  <div>
    <label style={{ display:'block', fontSize:'0.78rem', color:'#00b37e', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>{label}</label>
    {options ? (
      <select value={value ?? ''} onChange={e => onChange(field, e.target.value)} style={fieldStyle}>
        <option value="">— Selecione —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} value={value ?? ''} onChange={e => onChange(field, e.target.value)} style={fieldStyle} placeholder={placeholder} />
    )}
  </div>
);

// ─── Badge de status ──────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS['registrado'];
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding:'4px 14px', borderRadius:20, fontSize:'0.82rem', fontWeight:700 }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────

const AgroEventoView = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = uid === 'novo';

  useEffect(() => {
    if (!isNew && location.state?.viewMode) {
      setEditMode(false);
      setEditData(null);
    }
  }, [uid, isNew, location.state]);

  const EMPTY_EVENTO = {
    titulo: '', categoria: 'operacao_agricola',
    data_evento: new Date().toISOString().split('T')[0],
    status_kanban: 'agendado', origem: 'manual',
    valor: '', quantidade: '', valor_unitario: '', valor_total: '', contraparte: '',
    area_texto: '', uid_area: '', cultura_texto: '', uid_cultura: '',
    veiculo_texto: '', uid_veiculo: '', insumo_texto: '', produto_texto: '',
    categoria_custo: '', tipo_manutencao: '', combustivel: '',
    volume_litros: '', hodometro: '', observacao_complementar: '',
    // ── NOVOS ──────────────────────────────────────────────────────────────
    tipo_operacao_texto: '',
    data_programada: '', uid_usuario_programou: '',
    data_inicio: '',     uid_usuario_iniciou: '',
    data_conclusao: '',  uid_usuario_concluiu: '',
  };

  const [evento,    setEvento]    = useState<any>(null);
  const [editMode,  setEditMode]  = useState(isNew);
  const [editData,  setEditData]  = useState<any>(isNew ? { ...EMPTY_EVENTO } : null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');

  const [areas,    setAreas]    = useState<{ uid_area: string; nome: string }[]>([]);
  const [culturas, setCulturas] = useState<{ uid_cultura: string; nome: string }[]>([]);
  const [veiculos, setVeiculos] = useState<{ uid_veiculo: string; modelo: string; marca?: string }[]>([]);
  const [usuarios, setUsuarios] = useState<{ uid_usuario: string; nome: string }[]>([]);

  useEffect(() => {
    if (isNew) return;
    setEvento(null);
    api.get(`/agro/eventos/${uid}`).then(res => setEvento(res.data.data));
  }, [uid, isNew]);

  useEffect(() => {
    if (!editMode && !isNew) return;
    const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
    const q = uidFazenda ? `?uid_unidade_producao=${uidFazenda}` : '';
    api.get(`/agro/areas${q}`).then(r    => setAreas(r.data.data    || [])).catch(() => {});
    api.get(`/agro/culturas${q}`).then(r => setCulturas(r.data.data || [])).catch(() => {});
    api.get(`/agro/veiculos${q}`).then(r => setVeiculos(r.data.data || [])).catch(() => {});
    api.get(`/usuarios${q}`).then(r      => setUsuarios(r.data.data || [])).catch(() => {});
  }, [editMode, isNew]);

  const handleEdit = () => { setEditData({ ...evento }); setEditMode(true); setSaveError(''); };
  const handleCancelEdit = () => {
    if (isNew) { navigate('/agro/eventos'); return; }
    setEditMode(false); setEditData(null); setSaveError('');
  };

  const handleFieldChange = (field: string, val: any) =>
    setEditData((prev: any) => ({ ...prev, [field]: val }));

  const handleAreaChange = (_: string, val: string) => {
    const area = areas.find(a => a.uid_area === val);
    setEditData((prev: any) => ({ ...prev, uid_area: val, area_texto: area?.nome || val }));
  };
  const handleCulturaChange = (_: string, val: string) => {
    const cultura = culturas.find(c => c.uid_cultura === val);
    setEditData((prev: any) => ({ ...prev, uid_cultura: val, cultura_texto: cultura?.nome || val }));
  };
  const handleVeiculoChange = (_: string, val: string) => {
    const v = veiculos.find(x => x.uid_veiculo === val);
    const label = v ? `${v.marca ? v.marca + ' ' : ''}${v.modelo}`.trim() : val;
    setEditData((prev: any) => ({ ...prev, uid_veiculo: val, veiculo_texto: label }));
  };

  const handleSave = async () => {
    setSaving(true); setSaveError('');
    try {
      const payload = { ...editData };
      ['uid_evento','uid_unidade_producao','uid_usuario','syn_cadmom','syn_canmom',
       'usuario_nome','mensagem_original','nome_programou','nome_iniciou','nome_concluiu'
      ].forEach(k => delete payload[k]);

      if (isNew) {
        const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
        if (uidFazenda) payload.uid_unidade_producao = uidFazenda;
        const res = await api.post('/agro/eventos', payload);
        const novoUid = res.data.data?.uid_evento;
        if (novoUid) {
          navigate(`/agro/eventos/${novoUid}`, { replace: true, state: { viewMode: true } });
        } else {
          navigate('/agro/eventos', { replace: true });
        }
      } else {
        await api.patch(`/agro/eventos/${uid}`, payload);
        setEvento({ ...evento, ...editData });
        setEditMode(false); setEditData(null);
      }
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && !evento) return <div style={{ padding:40, textAlign:'center', color:'#999' }}>Carregando...</div>;

  const D     = editMode ? editData : (evento || {});
  const emoji = EMOJI_CATEGORIA[(evento || editData)?.categoria] || '📝';
  const tipoOpColor = D.tipo_operacao_texto ? (CORES_TIPO_OP[D.tipo_operacao_texto] || '#607d8b') : null;

  const opcoesArea        = areas.map(a    => ({ value: a.uid_area,    label: a.nome }));
  const opcoesCultura     = culturas.map(c => ({ value: c.uid_cultura, label: c.nome }));
  const opcoesVeiculo     = veiculos.map(v => ({ value: v.uid_veiculo, label: `${v.marca ? v.marca+' ' : ''}${v.modelo}`.trim() }));
  const opcoesCombustivel = COMBUSTIVEIS.map(c     => ({ value: c, label: c }));
  const opcoesManutencao  = TIPOS_MANUTENCAO.map(t => ({ value: t, label: t }));
  const opcoesTipoOp      = TIPOS_OPERACAO_OPCOES.map(t => ({ value: t, label: t }));
  const opcoesUsuarios    = usuarios.map(u => ({ value: u.uid_usuario, label: u.nome }));

  // Verifica se tem dados de ciclo de vida para exibir a seção
  const temCicloVida = D.data_programada || D.data_inicio || D.data_conclusao ||
                       D.nome_programou  || D.nome_iniciou  || D.nome_concluiu ||
                       D.tipo_operacao_texto;

  return (
    <div className={viewStyles.container}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className={viewStyles.headerTitle}>
        <h1 className={viewStyles.greenTitle}>{emoji} {isNew ? 'Novo Evento' : (evento?.titulo || '')}</h1>
        <div className={viewStyles.actionsHeader} style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* Badge de status no header (modo view) */}
          {!editMode && !isNew && evento?.status_kanban && (
            <StatusBadge status={evento.status_kanban} />
          )}
          {!editMode && !isNew ? (
            <>
              <button className={viewStyles.blueBtn} onClick={handleEdit} title="Editar"><Edit fontSize="small" /></button>
              <button className={viewStyles.blueBtn} onClick={() => navigate(-1)}><ArrowBack fontSize="small" /></button>
            </>
          ) : (
            <button className={viewStyles.blueBtn} onClick={() => navigate(-1)}><ArrowBack fontSize="small" /></button>
          )}
        </div>
      </div>

      {/* ── DADOS GERAIS ──────────────────────────────────────────────────── */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader}>DADOS GERAIS</div>
        <div className={viewStyles.sectionBody}>
          <div className={viewStyles.gridInfo}>
            {editMode ? (
              <>
                <CampoEdit label="Título" field="titulo" value={D.titulo} onChange={handleFieldChange} />
                <CampoEdit label="Categoria" field="categoria" value={D.categoria} onChange={handleFieldChange} options={[
                  { value:'operacao_agricola', label:'🚜 Operação Agrícola' },
                  { value:'custo',             label:'💸 Custo' },
                  { value:'receita',           label:'💰 Receita' },
                  { value:'manutencao',        label:'🔧 Manutenção' },
                  { value:'abastecimento',     label:'⛽ Abastecimento' },
                  { value:'outros',            label:'📝 Outros' },
                ]} />
                <CampoEdit label="Data do Evento" field="data_evento" type="date" value={D.data_evento} onChange={handleFieldChange} />
                <CampoEdit label="Status" field="status_kanban" value={D.status_kanban} onChange={handleFieldChange} options={[
                  { value:'agendado',     label:'🟣 Agendado' },
                  { value:'registrado',   label:'🔵 Registrado' },
                  { value:'em_andamento', label:'🟡 Em andamento' },
                  { value:'concluido',    label:'🟢 Concluído' },
                  { value:'cancelado',    label:'⚫ Cancelado' },
                ]} />
              </>
            ) : (
              <>
                <Campo label="Categoria"    value={LABEL_CATEGORIA[evento.categoria] || evento.categoria} />
                <Campo label="Data do Evento" value={formatData(evento.data_evento)} />
                <Campo label="Status">
                  <StatusBadge status={evento.status_kanban} />
                </Campo>
                <Campo label="Origem"       value={evento.origem} />
                <Campo label="Criado por"   value={evento.usuario_nome || evento.uid_usuario} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── FINANCEIRO ────────────────────────────────────────────────────── */}
      {(D.valor || D.valor_total || editMode || isNew) && (
        <div className={viewStyles.sectionCard}>
          <div className={viewStyles.sectionHeader}>FINANCEIRO</div>
          <div className={viewStyles.sectionBody}>
            <div className={viewStyles.gridInfo}>
              {editMode ? (
                <>
                  <CampoEdit label="Valor"          field="valor"         type="number" value={D.valor}         onChange={handleFieldChange} />
                  <CampoEdit label="Quantidade"     field="quantidade"    type="number" value={D.quantidade}    onChange={handleFieldChange} />
                  <CampoEdit label="Valor Unitário" field="valor_unitario" type="number" value={D.valor_unitario} onChange={handleFieldChange} />
                  <CampoEdit label="Valor Total"    field="valor_total"   type="number" value={D.valor_total}   onChange={handleFieldChange} />
                  <CampoEdit label="Contraparte"    field="contraparte"   value={D.contraparte}                 onChange={handleFieldChange} />
                </>
              ) : (
                <>
                  <Campo label="Valor"          value={formatBRL(evento.valor)} />
                  <Campo label="Quantidade"     value={evento.quantidade} />
                  <Campo label="Valor Unitário" value={formatBRL(evento.valor_unitario)} />
                  <Campo label="Valor Total"    value={formatBRL(evento.valor_total)} />
                  <Campo label="Contraparte"    value={evento.contraparte} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── CONTEXTO AGRÍCOLA ─────────────────────────────────────────────── */}
      {(D.area_texto || D.cultura_texto || D.veiculo_texto || D.insumo_texto || D.tipo_operacao_texto || editMode || isNew) && (
        <div className={viewStyles.sectionCard}>
          <div className={viewStyles.sectionHeader}>CONTEXTO AGRÍCOLA</div>
          <div className={viewStyles.sectionBody}>
            <div className={viewStyles.gridInfo}>
              {editMode ? (
                <>
                  {/* Tipo de operação — só para categoria operacao_agricola */}
                  {(D.categoria === 'operacao_agricola' || !D.categoria) && (
                    <CampoEdit
                      label="Tipo de Operação"
                      field="tipo_operacao_texto"
                      value={D.tipo_operacao_texto}
                      options={opcoesTipoOp}
                      onChange={handleFieldChange}
                    />
                  )}
                  <div>
                    <label style={{ display:'block', fontSize:'0.78rem', color:'#00b37e', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Área</label>
                    <select value={D.uid_area || ''} onChange={e => handleAreaChange('uid_area', e.target.value)} style={fieldStyle}>
                      <option value="">— Selecione —</option>
                      {opcoesArea.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!D.uid_area && D.area_texto && <p style={{ margin:'4px 0 0', fontSize:'0.75rem', color:'#aaa' }}>Atual: {D.area_texto}</p>}
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'0.78rem', color:'#00b37e', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Cultura</label>
                    <select value={D.uid_cultura || ''} onChange={e => handleCulturaChange('uid_cultura', e.target.value)} style={fieldStyle}>
                      <option value="">— Selecione —</option>
                      {opcoesCultura.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!D.uid_cultura && D.cultura_texto && <p style={{ margin:'4px 0 0', fontSize:'0.75rem', color:'#aaa' }}>Atual: {D.cultura_texto}</p>}
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'0.78rem', color:'#00b37e', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Veículo</label>
                    <select value={D.uid_veiculo || ''} onChange={e => handleVeiculoChange('uid_veiculo', e.target.value)} style={fieldStyle}>
                      <option value="">— Selecione —</option>
                      {opcoesVeiculo.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!D.uid_veiculo && D.veiculo_texto && <p style={{ margin:'4px 0 0', fontSize:'0.75rem', color:'#aaa' }}>Atual: {D.veiculo_texto}</p>}
                  </div>
                  <CampoEdit label="Insumo"             field="insumo_texto"   value={D.insumo_texto}   onChange={handleFieldChange} />
                  <CampoEdit label="Produto"            field="produto_texto"  value={D.produto_texto}  onChange={handleFieldChange} />
                  <CampoEdit label="Categoria de Custo" field="categoria_custo" value={D.categoria_custo} onChange={handleFieldChange} />
                </>
              ) : (
                <>
                  {evento.tipo_operacao_texto && (
                    <Campo label="Tipo de Operação">
                      <span style={{ background:`${tipoOpColor}22`, color: tipoOpColor!, padding:'4px 14px', borderRadius:16, fontSize:'0.85rem', fontWeight:700 }}>
                        {evento.tipo_operacao_texto}
                      </span>
                    </Campo>
                  )}
                  <Campo label="Área"               value={evento.area_texto} />
                  <Campo label="Cultura"            value={evento.cultura_texto} />
                  <Campo label="Veículo"            value={evento.veiculo_texto} />
                  <Campo label="Insumo"             value={evento.insumo_texto} />
                  <Campo label="Produto"            value={evento.produto_texto} />
                  <Campo label="Categoria de Custo" value={evento.categoria_custo} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── DETALHES OPERACIONAIS ─────────────────────────────────────────── */}
      {(D.tipo_manutencao || D.combustivel || editMode || isNew) && (
        <div className={viewStyles.sectionCard}>
          <div className={viewStyles.sectionHeader}>DETALHES OPERACIONAIS</div>
          <div className={viewStyles.sectionBody}>
            <div className={viewStyles.gridInfo}>
              {editMode ? (
                <>
                  <CampoEdit label="Tipo de Manutenção" field="tipo_manutencao" value={D.tipo_manutencao} options={opcoesManutencao} onChange={handleFieldChange} />
                  <CampoEdit label="Combustível" field="combustivel" value={D.combustivel} options={opcoesCombustivel} onChange={handleFieldChange} />
                  <CampoEdit label="Volume (L)"  field="volume_litros" type="number" value={D.volume_litros} onChange={handleFieldChange} />
                  <CampoEdit label="Hodômetro"   field="hodometro"     type="number" value={D.hodometro}     onChange={handleFieldChange} />
                </>
              ) : (
                <>
                  <Campo label="Tipo de Manutenção" value={evento.tipo_manutencao} />
                  <Campo label="Combustível"        value={evento.combustivel} />
                  <Campo label="Volume (L)"         value={evento.volume_litros} />
                  <Campo label="Hodômetro"          value={evento.hodometro} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ANDAMENTO ─────────────────────────────────────────────────────── */}
      {(temCicloVida || editMode || isNew) && (
        <div className={viewStyles.sectionCard}>
          <div className={viewStyles.sectionHeader}>ANDAMENTO</div>
          <div className={viewStyles.sectionBody}>
            {editMode ? (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                {/* Programado */}
                <div style={{ background:'#f9f0ff', borderRadius:10, padding:'12px' }}>
                  <p style={{ margin:'0 0 10px', fontSize:'0.75rem', fontWeight:700, color:'#9b59b6', textTransform:'uppercase' }}>📅 Programado</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div>
                      <label style={{ display:'block', fontSize:'0.73rem', color:'#9b59b6', fontWeight:700, marginBottom:3 }}>DATA PROGRAMADA</label>
                      <input type="datetime-local" value={D.data_programada ? D.data_programada.replace('Z','').slice(0,16) : ''} onChange={e => handleFieldChange('data_programada', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #9b59b6', background:'#fff' }} />
                    </div>
                    {opcoesUsuarios.length > 0 && (
                      <div>
                        <label style={{ display:'block', fontSize:'0.73rem', color:'#9b59b6', fontWeight:700, marginBottom:3 }}>RESPONSÁVEL</label>
                        <select value={D.uid_usuario_programou || ''} onChange={e => handleFieldChange('uid_usuario_programou', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #9b59b6', background:'#fff' }}>
                          <option value="">— Selecione —</option>
                          {opcoesUsuarios.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Iniciado */}
                <div style={{ background:'#fef9e7', borderRadius:10, padding:'12px' }}>
                  <p style={{ margin:'0 0 10px', fontSize:'0.75rem', fontWeight:700, color:'#f39c12', textTransform:'uppercase' }}>▶️ Em execução</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div>
                      <label style={{ display:'block', fontSize:'0.73rem', color:'#f39c12', fontWeight:700, marginBottom:3 }}>DATA DE INÍCIO</label>
                      <input type="datetime-local" value={D.data_inicio ? D.data_inicio.replace('Z','').slice(0,16) : ''} onChange={e => handleFieldChange('data_inicio', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #f39c12', background:'#fff' }} />
                    </div>
                    {opcoesUsuarios.length > 0 && (
                      <div>
                        <label style={{ display:'block', fontSize:'0.73rem', color:'#f39c12', fontWeight:700, marginBottom:3 }}>RESPONSÁVEL</label>
                        <select value={D.uid_usuario_iniciou || ''} onChange={e => handleFieldChange('uid_usuario_iniciou', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #f39c12', background:'#fff' }}>
                          <option value="">— Selecione —</option>
                          {opcoesUsuarios.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Concluído */}
                <div style={{ background:'#f0fff8', borderRadius:10, padding:'12px' }}>
                  <p style={{ margin:'0 0 10px', fontSize:'0.75rem', fontWeight:700, color:'#00b37e', textTransform:'uppercase' }}>✅ Concluído</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div>
                      <label style={{ display:'block', fontSize:'0.73rem', color:'#00b37e', fontWeight:700, marginBottom:3 }}>DATA DE CONCLUSÃO</label>
                      <input type="datetime-local" value={D.data_conclusao ? D.data_conclusao.replace('Z','').slice(0,16) : ''} onChange={e => handleFieldChange('data_conclusao', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #00b37e', background:'#fff' }} />
                    </div>
                    {opcoesUsuarios.length > 0 && (
                      <div>
                        <label style={{ display:'block', fontSize:'0.73rem', color:'#00b37e', fontWeight:700, marginBottom:3 }}>RESPONSÁVEL</label>
                        <select value={D.uid_usuario_concluiu || ''} onChange={e => handleFieldChange('uid_usuario_concluiu', e.target.value || null)} style={{ ...fieldStyle, border:'1px solid #00b37e', background:'#fff' }}>
                          <option value="">— Selecione —</option>
                          {opcoesUsuarios.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>

                {/* Fase Programado */}
                <div style={{ borderRadius:10, padding:'12px', background: evento.data_programada ? '#f9f0ff' : '#fafafa', border:`1px solid ${evento.data_programada ? '#9b59b680' : '#eee'}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                    <span style={{ fontSize:'1rem' }}>📅</span>
                    <span style={{ fontSize:'0.75rem', fontWeight:700, color: evento.data_programada ? '#9b59b6' : '#ccc', textTransform:'uppercase' }}>Programado</span>
                  </div>
                  {evento.data_programada ? (
                    <>
                      <p style={{ margin:0, fontWeight:700, fontSize:'0.9rem', color:'#9b59b6' }}>{formatData(evento.data_programada, true)}</p>
                      {evento.nome_programou && <p style={{ margin:'4px 0 0', fontSize:'0.78rem', color:'#888' }}>👤 {evento.nome_programou}</p>}
                    </>
                  ) : (
                    <p style={{ margin:0, fontSize:'0.8rem', color:'#ccc' }}>Não definido</p>
                  )}
                </div>

                {/* Fase Em execução */}
                <div style={{ borderRadius:10, padding:'12px', background: evento.data_inicio ? '#fef9e7' : '#fafafa', border:`1px solid ${evento.data_inicio ? '#f39c1280' : '#eee'}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                    <span style={{ fontSize:'1rem' }}>▶️</span>
                    <span style={{ fontSize:'0.75rem', fontWeight:700, color: evento.data_inicio ? '#f39c12' : '#ccc', textTransform:'uppercase' }}>Em execução</span>
                  </div>
                  {evento.data_inicio ? (
                    <>
                      <p style={{ margin:0, fontWeight:700, fontSize:'0.9rem', color:'#f39c12' }}>{formatData(evento.data_inicio, true)}</p>
                      {evento.nome_iniciou && <p style={{ margin:'4px 0 0', fontSize:'0.78rem', color:'#888' }}>👤 {evento.nome_iniciou}</p>}
                    </>
                  ) : (
                    <p style={{ margin:0, fontSize:'0.8rem', color:'#ccc' }}>Não iniciado</p>
                  )}
                </div>

                {/* Fase Concluído */}
                <div style={{ borderRadius:10, padding:'12px', background: evento.data_conclusao ? '#f0fff8' : '#fafafa', border:`1px solid ${evento.data_conclusao ? '#00b37e80' : '#eee'}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                    <span style={{ fontSize:'1rem' }}>✅</span>
                    <span style={{ fontSize:'0.75rem', fontWeight:700, color: evento.data_conclusao ? '#00b37e' : '#ccc', textTransform:'uppercase' }}>Concluído</span>
                  </div>
                  {evento.data_conclusao ? (
                    <>
                      <p style={{ margin:0, fontWeight:700, fontSize:'0.9rem', color:'#00b37e' }}>{formatData(evento.data_conclusao, true)}</p>
                      {evento.nome_concluiu && <p style={{ margin:'4px 0 0', fontSize:'0.78rem', color:'#888' }}>👤 {evento.nome_concluiu}</p>}
                    </>
                  ) : (
                    <p style={{ margin:0, fontSize:'0.8rem', color:'#ccc' }}>Não concluído</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── OBSERVAÇÃO ────────────────────────────────────────────────────── */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader}>OBSERVAÇÃO</div>
        <div className={viewStyles.sectionBody}>
          {editMode ? (
            <textarea
              value={editData.observacao_complementar || ''}
              onChange={e => handleFieldChange('observacao_complementar', e.target.value)}
              rows={3}
              style={{ width:'100%', padding:'10px', borderRadius:8, border:'1px solid #00b37e', outline:'none', fontSize:'0.9rem', background:'#f0fff8', resize:'vertical', boxSizing:'border-box' }}
              placeholder="Observações adicionais..."
            />
          ) : (
            <p style={{ margin:0, color:'#555' }}>
              {evento.observacao_complementar || <span style={{ color:'#bbb' }}>Nenhuma observação.</span>}
            </p>
          )}
        </div>
      </div>

      {/* ── Botões Salvar / Cancelar ───────────────────────────────────────── */}
      {editMode && (
        <div style={{ position:'sticky', bottom:16, display:'flex', gap:12, justifyContent:'flex-end', marginTop:16 }}>
          {saveError && <p style={{ alignSelf:'center', color:'#e74c3c', fontSize:'0.85rem', margin:0 }}>{saveError}</p>}
          <button onClick={handleCancelEdit} disabled={saving} style={{
            display:'flex', alignItems:'center', gap:6, padding:'10px 24px', borderRadius:10,
            border:'1px solid #ddd', background:'#fff', cursor:'pointer', fontWeight:600, color:'#555', fontSize:'0.9rem',
          }}>
            <Close fontSize="small" /> Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            display:'flex', alignItems:'center', gap:6, padding:'10px 28px', borderRadius:10,
            border:'none', background: saving ? '#aaa' : '#00b37e', color:'#fff',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:'0.9rem',
            boxShadow:'0 4px 12px rgba(0,179,126,0.3)',
          }}>
            <Save fontSize="small" /> {saving ? (isNew ? 'Criando...' : 'Salvando...') : (isNew ? 'Criar evento' : 'Salvar')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgroEventoView;